import { ShoppingBag } from 'lucide-react';

import type { AssetResponse } from '../../api';
import { smallUrlOf, type AssetUrlLookup } from '../grid/useAssetUrls';
import type { Basket } from './useBasket';

/**
 * The mini-cart: a docked summary that appears once the basket has something
 * in it, and is the only way into checkout.
 *
 * Docked to the viewport rather than placed in the page, for the reason every
 * storefront does the same — picking happens while scrolling a long grid, and a
 * basket you have to scroll back up to reach is one people forget they filled.
 * It replaces nothing: the row panel keeps its width, the grid keeps its
 * scroll, and the bar rides above both.
 *
 * It deliberately does not list the basket. A summary that tries to be the
 * basket ends up as a third scrolling region competing with the grid and the
 * row panel; the full picture lives one click away in the drawer, which has the
 * room to group by row and say what each line will cost in production time.
 */

interface BasketBarProps {
  basket: Basket;
  /** Every asset the basket refers to, resolved across tabs and reloads. */
  assetById: Map<string, AssetResponse>;
  urls: AssetUrlLookup;
  onReview: () => void;
}

/** How many thumbnails the stack shows before it gives up and counts. */
const STACK_LIMIT = 4;

export function BasketBar({ basket, assetById, urls, onReview }: BasketBarProps) {
  if (basket.count === 0) return null;

  // Deduped: the same source image ordered for three rows is three lines but one
  // picture, and a stack showing it three times reads as a mistake.
  const distinctAssetIds = Array.from(new Set(basket.lines.map((line) => line.assetId)));
  const stack = distinctAssetIds.slice(0, STACK_LIMIT);
  const overflow = distinctAssetIds.length - stack.length;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center px-4 pb-4">
      <div className="pointer-events-auto flex items-center gap-4 rounded-xl border border-slate-200 bg-white/95 py-2.5 pl-3 pr-2.5 shadow-[0_8px_30px_rgba(15,23,42,0.12)] backdrop-blur">
        {/* Overlapping thumbnails: the one bit of shop grammar worth keeping,
            because it answers "what did I put in there?" without opening it. */}
        <div className="flex items-center -space-x-2.5">
          {stack.map((assetId) => {
            const asset = assetById.get(assetId);
            const url = smallUrlOf(urls, assetId);
            return (
              <span
                key={assetId}
                title={asset?.filename_original ?? undefined}
                className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg border-2 border-white bg-slate-100 ring-1 ring-slate-200"
              >
                {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : null}
              </span>
            );
          })}
          {overflow > 0 && (
            <span className="flex h-9 w-9 items-center justify-center rounded-lg border-2 border-white bg-slate-800 text-[11px] font-semibold text-white ring-1 ring-slate-200">
              +{overflow}
            </span>
          )}
        </div>

        <div className="min-w-0">
          {/* The count is the localisation count, not the asset count: it is
              what production will actually be asked to make, and the two differ
              the moment one asset is ordered for two rows. */}
          <p className="text-[13px] font-semibold text-slate-800">
            {basket.count} localisation{basket.count === 1 ? '' : 's'}
          </p>
          <p className="text-[11.5px] text-slate-500">
            across {basket.rowCount} row{basket.rowCount === 1 ? '' : 's'}
          </p>
        </div>

        <button
          type="button"
          onClick={basket.clear}
          className="rounded-lg px-2.5 py-1.5 text-[12px] font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          Empty
        </button>

        <button
          type="button"
          onClick={onReview}
          className="inline-flex items-center gap-2 rounded-lg bg-[#0014cc] px-4 py-2 text-[12.5px] font-semibold text-white transition-colors hover:bg-[#0010a3]"
        >
          <ShoppingBag className="h-3.5 w-3.5" strokeWidth={2} />
          Review order
        </button>
      </div>
    </div>
  );
}
